import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { ArrowLeft, Mail, Phone, Home, Calendar, MessageSquare } from 'lucide-react'

interface Lead {
  id: string
  name: string
  email: string
  phone: string
  message: string
  listing_code: string
  created_at: string
  listings: {
    address_street: string
    address_city: string
  }
}

export default function LeadDetail() {
  const { id } = useParams()
  const [lead, setLead] = useState<Lead | null>(null)
  const [loading, setLoading] = useState(true)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    fetchLead()
  }, [id])

  const fetchLead = async () => {
    const { data, error } = await supabase
      .from('leads')
      .select(`
        *,
        listings:listing_id(address_street, address_city)
      `)
      .eq('id', id)
      .single()

    if (!error && data) {
      setLead(data)
    }
    setLoading(false)
  }

  const markAsContacted = async () => {
    await supabase.from('leads').update({ status: 'contacted' }).eq('id', id)
    fetchLead()
  }

  if (loading) return <div className="p-8">Loading...</div>
  if (!lead) return <div className="p-8">Lead not found</div>

  return (
    <div className="p-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/leads" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft size={24} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{lead.name}</h1>
          <p className="text-gray-500 flex items-center gap-2">
            <Calendar size={16} />
            {new Date(lead.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail size={20} className="text-gray-400" />
              <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                {lead.email}
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone size={20} className="text-gray-400" />
              <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                {lead.phone}
              </a>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <a
              href={`mailto:${lead.email}`}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-center hover:bg-primary-dark"
            >
              Send Email
            </a>
            <a
              href={`tel:${lead.phone}`}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-center hover:bg-gray-50"
            >
              Call
            </a>
          </div>
        </div>

        {/* Source */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Home size={20} />
            Property Interest
          </h2>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-medium">
              Code: <span className="font-mono">{lead.listing_code}</span>
            </p>
            {lead.listings && (
              <p className="text-gray-600 mt-1">
                {lead.listings.address_street}<br />
                {lead.listings.address_city}
              </p>
            )}
          </div>

          <Link
            to={`/listings`}
            className="mt-4 inline-block text-primary hover:underline"
          >
            View Property →
          </Link>
        </div>

        {/* Message */}
        {lead.message && (
          <div className="md:col-span-2 bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare size={20} />
              Message from Lead
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">{lead.message}</p>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="md:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Notes</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this lead..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
          <div className="mt-4 flex gap-3">
            <button
              onClick={markAsContacted}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              Mark as Contacted
            </button>
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Save Notes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
